// src/components/mensagens/ClientCard.jsx
// ============================================================================
// 🎯 COMPONENTE: ClientCard
// 📋 Função: Card de cliente com informações e ações rápidas
// 📱 Responsivo: Adaptado para mobile e desktop com feedback tátil
// ============================================================================

import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';  // ✅ IMPORT ADICIONADO
import {
  Phone,
  Clock,
  MessageCircle,
  AlertCircle,
  Mail,
  User,
  MapPin,
  Tag,
  Star,
  TrendingUp,
  Zap,
  Calendar
} from 'lucide-react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { QuickActions } from './QuickActions';
import {
  getInitials,
  formatPhoneDisplay,
  getTemperatureColor,
  getStatusColor,
  getTimeAgo
} from './helpers';

/**
 * 🎯 Componente de card de cliente
 * @param {Object} props
 * @param {Object} props.client - Dados do cliente
 * @param {Object} props.contactInfo - Informações de contato
 * @param {string} props.copiedId - ID do cliente com telefone copiado
 * @param {string} props.selectedClient - ID do cliente selecionado
 * @param {Function} props.onCopyPhone - Callback para copiar telefone
 * @param {Function} props.onWhatsApp - Callback para enviar WhatsApp
 * @param {Function} props.onTemplateSelect - Callback para selecionar template
 * @param {Function} props.onShowHistory - Callback para mostrar histórico
 * @param {Function} props.setSelectedClient - Callback para setar cliente selecionado
 * @param {string} props.primaryColor - Cor primária do tema
 * @param {boolean} props.isMobile - Se está em mobile
 */
export const ClientCard = memo(({
  client,
  contactInfo,
  copiedId,
  selectedClient,
  onCopyPhone,
  onWhatsApp,
  onTemplateSelect,
  onShowHistory,
  setSelectedClient,
  primaryColor,
  isMobile
}) => {
  const navigate = useNavigate();  // ✅ HOOK ADICIONADO
  const [touchActive, setTouchActive] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleTouchStart = () => setTouchActive(true);
  const handleTouchEnd = () => setTouchActive(false);

  // Status da última mensagem
  const getLastMessageStatus = () => {
    if (!contactInfo?.lastMessage) return null;

    const msg = contactInfo.lastMessage;
    if (msg.direction === 'incoming') {
      return { label: 'Respondido', color: 'bg-green-100 text-green-700', icon: MessageCircle };
    }
    if (msg.status === 'read') {
      return { label: 'Lido', color: 'bg-purple-100 text-purple-700', icon: Star };
    }
    if (msg.status === 'delivered') {
      return { label: 'Entregue', color: 'bg-blue-100 text-blue-700', icon: Clock };
    }
    return { label: 'Enviado', color: 'bg-gray-100 text-gray-700', icon: Zap };
  };

  const statusBadge = getLastMessageStatus();
  const StatusIcon = statusBadge?.icon || AlertCircle;

  // Cor do avatar baseado na temperatura
  const getAvatarColor = () => {
    switch(client.temperature) {
      case 'Quente': return 'from-red-500 to-red-600';
      case 'Morno': return 'from-yellow-500 to-yellow-600';
      case 'Frio': return 'from-blue-500 to-blue-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  // Tags do cliente (se houver)
  const tags = client.tags || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    onClick={() => {
  if (!isMobile) {
    setExpanded(!expanded);
  }
  
  const lastMessageId = contactInfo?.lastMessage?.id;
  
  if (lastMessageId) {
    // Se tiver mensagem, vai para detalhes da mensagem
    navigate(`/mensagens/${lastMessageId}`);
  } else {
    // Se não tiver mensagem, abre o modal universal
    // Você precisa passar esta função via props
    if (onWhatsApp) {
      onWhatsApp(client, 'padrao');
    }
  }
}}
      className={`
        bg-white rounded-xl shadow-sm border border-gray-200 
        hover:shadow-md transition-all cursor-pointer
        ${touchActive ? 'scale-[0.99] bg-slate-50' : ''}
        ${isMobile ? 'p-3' : 'p-4'}
      `}
    >
      <div className={`
        flex ${isMobile ? 'flex-col gap-3' : 'flex-col lg:flex-row lg:items-center'} 
        justify-between gap-3
      `}>
        
        {/* Informações do Cliente */}
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {/* Avatar com iniciais */}
          <div className={`
            rounded-full flex items-center justify-center text-white font-medium flex-shrink-0
            bg-gradient-to-br ${getAvatarColor()}
            ${isMobile ? 'w-10 h-10 text-xs' : 'w-12 h-12 lg:w-10 lg:h-10 text-sm'}
          `}>
            {getInitials(client.name)}
          </div>

          <div className="flex-1 min-w-0">
            {/* Nome e badges */}
            <div className="flex items-center gap-1 flex-wrap">
              <h3 className={`
                font-semibold text-gray-800 truncate max-w-[200px]
                ${isMobile ? 'text-sm' : ''}
              `}>
                {client.name || 'Sem nome'}
              </h3>
              
              {/* Badge de Status */}
              <span className={`
                px-2 py-0.5 rounded-full font-medium ${getStatusColor(client.status)}
                ${isMobile ? 'text-[9px]' : 'text-xs'}
              `}>
                {isMobile 
                  ? (client.status || 'Lead').substring(0, 3) 
                  : (client.status || 'Lead')
                }
              </span>

              {/* Badge de Temperatura */}
              <span className={`
                px-2 py-0.5 rounded-full border ${getTemperatureColor(client.temperature)}
                ${isMobile ? 'text-[9px]' : 'text-xs'}
              `}>
                {isMobile 
                  ? (client.temperature || 'Morno').substring(0, 3) 
                  : (client.temperature || 'Morno')
                }
              </span>
            </div>

            {/* Contatos */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1">
              {/* Telefone */}
              {client.phone ? (
                <span className={`
                  flex items-center gap-1 text-gray-600
                  ${isMobile ? 'text-xs' : 'text-sm'}
                `}>
                  <Phone size={isMobile ? 12 : 14} className="text-gray-400 flex-shrink-0" />
                  <span className="truncate">{formatPhoneDisplay(client.phone)}</span>
                </span>
              ) : (
                <span className={`
                  flex items-center gap-1 text-gray-400 italic
                  ${isMobile ? 'text-xs' : 'text-sm'}
                `}>
                  <Phone size={isMobile ? 12 : 14} className="flex-shrink-0" />
                  <span>Sem telefone</span>
                </span>
              )}

              {/* Email (apenas desktop) */}
              {client.email && !isMobile && (
                <span className="flex items-center gap-1 text-gray-600 text-sm">
                  <span className="text-gray-300 hidden sm:inline">•</span>
                  <Mail size={14} className="text-gray-400 flex-shrink-0" />
                  <span className="truncate max-w-[200px]">{client.email}</span>
                </span>
              )}
            </div>

            {/* Badges de Contato */}
            <div className="flex items-center gap-1 mt-2 overflow-x-auto pb-1 scrollbar-hide">
              {contactInfo ? (
                <>
                  {/* Tempo desde último contato */}
                  <span className={`
                    bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full 
                    flex items-center gap-1 whitespace-nowrap
                    ${isMobile ? 'text-[9px]' : 'text-xs'}
                  `}>
                    <Clock size={isMobile ? 10 : 12} />
                    {contactInfo.timeText}
                  </span>

                  {/* Total de mensagens */}
                  <span className={`
                    bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full 
                    flex items-center gap-1 whitespace-nowrap
                    ${isMobile ? 'text-[9px]' : 'text-xs'}
                  `}>
                    <MessageCircle size={isMobile ? 10 : 12} />
                    {contactInfo.totalMessages}
                  </span>

                  {/* Status da última mensagem */}
                  {statusBadge && (
                    <span className={`
                      px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap
                      ${statusBadge.color}
                      ${isMobile ? 'text-[9px]' : 'text-xs'}
                    `}>
                      <StatusIcon size={isMobile ? 10 : 12} />
                      {isMobile ? statusBadge.label.substring(0, 3) : statusBadge.label}
                    </span>
                  )}
                </>
              ) : (
                <span className={`
                  bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full 
                  flex items-center gap-1 whitespace-nowrap
                  ${isMobile ? 'text-[9px]' : 'text-xs'}
                `}>
                  <AlertCircle size={isMobile ? 10 : 12} />
                  {isMobile ? 'Novo' : 'Nunca contactado'}
                </span>
              )}
            </div>

            {/* Informações expandidas (apenas desktop) */}
            {expanded && !isMobile && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 pt-3 border-t border-gray-100"
              >
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {/* Tipo de cliente */}
                  {client.type && (
                    <div className="flex items-center gap-1">
                      <User size={12} className="text-gray-400" />
                      <span className="text-gray-600">Tipo: {client.type}</span>
                    </div>
                  )}

                  {/* Última interação */}
                  {client.lastInteraction && (
                    <div className="flex items-center gap-1">
                      <Calendar size={12} className="text-gray-400" />
                      <span className="text-gray-600">
                        Última interação: {new Date(client.lastInteraction).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  )}

                  {/* Score do cliente */}
                  {client.score !== undefined && (
                    <div className="flex items-center gap-1">
                      <TrendingUp size={12} className="text-gray-400" />
                      <span className="text-gray-600">Score: {client.score}</span>
                    </div>
                  )}

                  {/* Cidade */}
                  {client.city && (
                    <div className="flex items-center gap-1">
                      <MapPin size={12} className="text-gray-400" />
                      <span className="text-gray-600">{client.city}</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px]"
                      >
                        <Tag size={8} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Ações Rápidas */}
        <QuickActions
          client={client}
          contactInfo={contactInfo}
          copiedId={copiedId}
          selectedClient={selectedClient}
          onCopyPhone={onCopyPhone}
          onWhatsApp={onWhatsApp}
          onTemplateSelect={onTemplateSelect}
          onShowHistory={onShowHistory}
          setSelectedClient={setSelectedClient}
          primaryColor={primaryColor}
          isMobile={isMobile}
        />
      </div>

      {/* Barra de progresso visual (opcional) */}
      {client.progress && (
        <div className="mt-3 w-full h-1 bg-gray-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${client.progress}%` }}
            transition={{ duration: 0.5 }}
            className="h-full rounded-full"
            style={{ backgroundColor: primaryColor }}
          />
        </div>
      )}
    </motion.div>
  );
});

ClientCard.displayName = 'ClientCard';

// ============================================================================
// 🎯 EXPORTAÇÃO PADRÃO
// ============================================================================
// ============================================================================
// 🎯 COMPONENTE DE SKELETON (LOADING)
// ============================================================================
export const ClientCardSkeleton = ({ isMobile }) => {
  return (
    <div className={`
      bg-white rounded-xl border border-gray-200 animate-pulse
      ${isMobile ? 'p-3' : 'p-4'}
    `}>
      <div className="flex items-start gap-2">
        {/* Avatar Skeleton */}
        <div className={`
          rounded-full bg-gray-200 flex-shrink-0
          ${isMobile ? 'w-10 h-10' : 'w-12 h-12 lg:w-10 lg:h-10'}
        `} />

        <div className="flex-1 space-y-2">
          {/* Nome Skeleton */}
          <div className={`
            bg-gray-200 rounded w-3/4
            ${isMobile ? 'h-4' : 'h-5'}
          `} />

          {/* Telefone Skeleton */}
          <div className={`
            bg-gray-200 rounded w-1/2
            ${isMobile ? 'h-3' : 'h-4'}
          `} />

          {/* Badges Skeleton */}
          <div className="flex gap-1">
            <div className={`
              bg-gray-200 rounded-full
              ${isMobile ? 'w-12 h-4' : 'w-16 h-5'}
            `} />
            <div className={`
              bg-gray-200 rounded-full
              ${isMobile ? 'w-12 h-4' : 'w-16 h-5'}
            `} />
          </div>
        </div>

        {/* Actions Skeleton */}
        <div className="flex gap-1">
          <div className={`
            bg-gray-200 rounded-lg
            ${isMobile ? 'w-8 h-8' : 'w-9 h-9'}
          `} />
          <div className={`
            bg-gray-200 rounded-lg
            ${isMobile ? 'w-8 h-8' : 'w-9 h-9'}
          `} />
        </div>
      </div>
    </div>
  );
};

/**
 * 🎯 Componente de lista de clientes
 * @param {Object} props
 * @param {Array} props.clients - Lista de clientes
 * @param {Object} props.contactInfoMap - Mapa de informações de contato
 * @param {Object} props.actionProps - Props para as ações
 */
export const ClientList = ({
  clients = [],
  contactInfoMap = {},
  actionProps = {}
}) => {
  const isMobile = useIsMobile();

  if (clients.length === 0) {
    return (
      <div className={`
        bg-white rounded-xl border border-gray-200 text-center
        ${isMobile ? 'p-8' : 'p-12'}
      `}>
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <User size={24} className="text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Nenhum cliente encontrado
        </h3>
        <p className="text-sm text-gray-500">
          Tente ajustar os filtros ou buscar por outro termo.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {clients.map((client) => {
        const contactInfo = contactInfoMap[client.id] || null;

        return (
          <ClientCard
            key={client.id}
            client={client}
            contactInfo={contactInfo}
            isMobile={isMobile}
            {...actionProps}
          />
        );
      })}
    </div>
  );
};
export default ClientCard;